import { cn } from "@/lib/utils";
import { statusColorClass } from "@/components/attendance/attendance-cell";
import type { CellStatus, MonthlyCell, MonthlyRow } from "@/lib/types";
import { CATEGORY_LABELS, CELL_STATUS_LABELS } from "@/lib/types";
import {
  getMonthlyDaySessions,
  monthlyRowKey,
  sessionAbbrev,
  sessionTitle,
} from "@/lib/monthly";
import { formatPresencePct, summarizeMonthlyRow } from "@/lib/attendance-pct";
import { Badge } from "@/components/ui/badge";
import { ChevronRight } from "lucide-react";

function sessionBadgeLabel(
  status: CellStatus,
  lateMinutes?: number | null
): string {
  if (status === "L" && lateMinutes != null) return `L${lateMinutes}`;
  if (status === "pending") return "·";
  return status;
}

function statusTooltip(
  abbrev: string,
  title: string,
  status: CellStatus,
  lateMinutes?: number | null
): string {
  const statusLabel =
    status === "L" && lateMinutes != null
      ? `Late (${lateMinutes} min)`
      : status === "MS" && lateMinutes != null
        ? `Mahamantras submitted (was late ${lateMinutes} min)`
        : status === "FS" && lateMinutes != null
          ? `Fine submitted (was late ${lateMinutes} min)`
          : CELL_STATUS_LABELS[status];
  return `${abbrev} · ${title}: ${statusLabel}`;
}

function MonthlySessionBadge({
  status,
  lateMinutes,
  title,
  edge,
}: {
  status: CellStatus;
  lateMinutes?: number | null;
  title: string;
  edge?: "left" | "right" | "only";
}) {
  const text = sessionBadgeLabel(status, lateMinutes);
  const pending = text === "·";

  return (
    <span
      className={cn(
        "inline-flex min-w-[1.1rem] flex-1 items-center justify-center py-0.5 text-[9px] font-semibold tabular-nums leading-none",
        edge === "left" && "rounded-l-sm pl-0.5",
        edge === "right" && "rounded-r-sm pr-0.5",
        edge === "only" && "rounded-sm px-0.5",
        pending
          ? "bg-slate-50 text-slate-300"
          : statusColorClass(status)
      )}
      title={title}
    >
      {text}
    </span>
  );
}

function MonthlyCellView({
  cell,
  multiShift,
}: {
  cell: MonthlyCell | undefined;
  multiShift?: boolean;
}) {
  const sessions = getMonthlyDaySessions(cell);

  if (sessions.length === 0) {
    return <span className="text-slate-300">—</span>;
  }

  if (!multiShift || sessions.length === 1) {
    const s = sessions[0];
    const abbrev = sessionAbbrev(s.session, 0);
    const title = sessionTitle(s.session, s.label, 0);
    return (
      <MonthlySessionBadge
        status={s.status}
        lateMinutes={s.late_minutes}
        title={statusTooltip(abbrev, title, s.status, s.late_minutes)}
        edge="only"
      />
    );
  }

  return (
    <div
      className="inline-flex overflow-hidden rounded border border-slate-200/80 bg-white"
      title={sessions
        .map((s, i) =>
          statusTooltip(
            sessionAbbrev(s.session, i),
            sessionTitle(s.session, s.label, i),
            s.status,
            s.late_minutes
          )
        )
        .join(" · ")}
    >
      {sessions.map((s, i) => (
        <span key={`${s.session ?? i}-${i}`} className="contents">
          {i > 0 && <span className="w-px self-stretch bg-slate-200/80" aria-hidden />}
          <MonthlySessionBadge
            status={s.status}
            lateMinutes={s.late_minutes}
            title={statusTooltip(
              sessionAbbrev(s.session, i),
              sessionTitle(s.session, s.label, i),
              s.status,
              s.late_minutes
            )}
            edge={i === 0 ? "left" : i === sessions.length - 1 ? "right" : undefined}
          />
        </span>
      ))}
    </div>
  );
}

function pctClass(pct: number | null): string {
  if (pct == null) return "text-slate-400";
  if (pct >= 90) return "text-emerald-700";
  if (pct >= 75) return "text-amber-700";
  return "text-red-700";
}

function shiftColumnLabel(label: string | null | undefined, index: number | null | undefined): string {
  if (label) {
    const lower = label.toLowerCase();
    if (lower.includes("morning")) return "AM";
    if (lower.includes("evening")) return "PM";
    if (lower.includes("whole")) return "Day";
    return label.length > 4 ? label.slice(0, 3) : label;
  }
  if (index === 0) return "AM";
  if (index === 1) return "PM";
  if (index != null) return `${index + 1}`;
  return "—";
}

interface MonthlyAttendanceTableProps {
  title: string;
  subtitle?: string;
  dates: string[];
  rows: MonthlyRow[];
  showCategory?: boolean;
  showShifts?: boolean;
  onRowClick?: (row: MonthlyRow) => void;
}

export function MonthlyAttendanceTable({
  title,
  subtitle,
  dates,
  rows,
  showCategory = false,
  showShifts = false,
  onRowClick,
}: MonthlyAttendanceTableProps) {
  const perRowShifts = showShifts && rows.some((r) => r.shift_label || r.shift_index != null);
  const stackedShifts = showShifts && !perRowShifts;

  return (
    <section className="space-y-2">
      <div>
        <h3 className="text-lg font-semibold">{title}</h3>
        {subtitle && (
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No people in this group.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-white">
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="sticky left-0 z-10 min-w-[140px] border-r bg-muted/95 px-3 py-2 text-left font-medium">
                  Name
                </th>
                {showCategory && (
                  <th className="sticky left-[140px] z-10 min-w-[72px] border-r bg-muted/95 px-2 py-2 text-left font-medium">
                    Group
                  </th>
                )}
                {perRowShifts && (
                  <th
                    className={cn(
                      "sticky z-10 min-w-[2rem] border-r bg-muted/95 px-1 py-2 text-center text-[10px] font-medium",
                      showCategory ? "left-[212px]" : "left-[140px]"
                    )}
                    title="Shift"
                  >
                    ⇄
                  </th>
                )}
                <th
                  className={cn(
                    "sticky z-10 min-w-[3rem] border-r bg-muted/95 px-1 py-2 text-center text-[10px] font-medium",
                    perRowShifts
                      ? showCategory
                        ? "left-[236px]"
                        : "left-[164px]"
                      : showCategory
                        ? "left-[212px]"
                        : "left-[140px]"
                  )}
                  title="Present count"
                >
                  P
                </th>
                <th className="min-w-[3rem] px-1 py-2 text-center text-[10px] font-medium">
                  L
                </th>
                <th className="min-w-[3rem] px-1 py-2 text-center text-[10px] font-medium">
                  A
                </th>
                <th
                  className="min-w-[3.5rem] border-r px-1 py-2 text-center text-[10px] font-medium"
                  title="Present % — P ÷ (P+A+L). MS/FS still count as late or absent"
                >
                  %
                </th>
                {dates.map((d) => (
                  <th
                    key={d}
                    className={cn(
                      "px-0.5 py-2 text-center text-[10px] font-medium text-muted-foreground",
                      stackedShifts ? "min-w-[2.25rem]" : "min-w-[2rem]"
                    )}
                    title={d}
                  >
                    {Number(d.slice(8, 10))}
                  </th>
                ))}
                {(["O", "S", "TP", "MS", "FS"] as const).map((code) => (
                  <th
                    key={code}
                    className="min-w-[2.25rem] border-l px-1 py-2 text-center text-[10px] font-medium"
                    title={CELL_STATUS_LABELS[code]}
                  >
                    {code}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const summary = summarizeMonthlyRow(row, dates);
                return (
                <tr
                  key={monthlyRowKey(row)}
                  className={cn(
                    "border-b last:border-0",
                    onRowClick && "cursor-pointer hover:bg-muted/30"
                  )}
                  onClick={() => onRowClick?.(row)}
                >
                  <td className="sticky left-0 z-[1] border-r bg-white px-3 py-1.5 font-medium whitespace-nowrap">
                    <span className="inline-flex items-center gap-1">
                      {row.person_name}
                      {onRowClick && (
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                    </span>
                  </td>
                  {showCategory && (
                    <td className="sticky left-[140px] z-[1] border-r bg-white px-2 py-1.5">
                      {row.category && (
                        <Badge variant="secondary" className="text-[10px]">
                          {CATEGORY_LABELS[row.category]}
                        </Badge>
                      )}
                    </td>
                  )}
                  {perRowShifts && (
                    <td
                      className={cn(
                        "sticky z-[1] border-r bg-white px-1 py-1.5 text-center text-[10px] font-medium text-muted-foreground",
                        showCategory ? "left-[212px]" : "left-[140px]"
                      )}
                    >
                      {shiftColumnLabel(row.shift_label, row.shift_index ?? null)}
                    </td>
                  )}
                  <td
                    className={cn(
                      "sticky z-[1] border-r bg-white px-1 py-1 text-center tabular-nums text-xs",
                      perRowShifts
                        ? showCategory
                          ? "left-[236px]"
                          : "left-[164px]"
                        : showCategory
                          ? "left-[212px]"
                          : "left-[140px]"
                    )}
                  >
                    {summary.present}
                  </td>
                  <td className="px-1 py-1 text-center tabular-nums text-xs">
                    {summary.late}
                  </td>
                  <td className="px-1 py-1 text-center tabular-nums text-xs">
                    {summary.absent}
                  </td>
                  <td
                    className={cn(
                      "border-r px-1 py-1 text-center text-xs font-semibold tabular-nums",
                      summary.all_out
                        ? "text-violet-700"
                        : pctClass(summary.present_pct)
                    )}
                  >
                    {formatPresencePct(summary.present_pct, summary.all_out)}
                  </td>
                  {dates.map((d) => (
                    <td key={d} className="px-0.5 py-1 text-center">
                      <MonthlyCellView
                        cell={row.cells[d]}
                        multiShift={stackedShifts}
                      />
                    </td>
                  ))}
                  <td className="border-l px-1 py-1 text-center tabular-nums text-xs">
                    {summary.on_leave}
                  </td>
                  <td className="px-1 py-1 text-center tabular-nums text-xs">
                    {summary.sick}
                  </td>
                  <td className="px-1 py-1 text-center tabular-nums text-xs">
                    {summary.taken_permission}
                  </td>
                  <td className="px-1 py-1 text-center tabular-nums text-xs">
                    {summary.mantra_submitted ?? 0}
                  </td>
                  <td className="px-1 py-1 text-center tabular-nums text-xs">
                    {summary.fine_submitted ?? 0}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
